import {
	type GetImpulseFormInput,
	type GetImpulseFormInputSetter,
	ImpulseFormList,
	ImpulseFormShape,
} from "~/tools/impulse-form";
import { setFormElementsInput } from "~/tools/impulse-form/set-form-elements-input";
import { isPresent } from "~/tools/is-present";
import type { Partial } from "~/tools/partial";

import { WidgetLegend } from "./legend/form-state";
import { WidgetRange } from "./range/form-state";
import { WidgetThresholdsMode, WidgetThresholdsStep } from "./thresholds/form-state";
import { WidgetYAxis } from "./y-axis/form-state";
import type { WidgetFormSync } from "./sync";

type BarChartFormState = ImpulseFormShape<{
	range: ImpulseFormShape<{
		min: WidgetRange;
		max: WidgetRange;
	}>;

	legend: WidgetLegend;

	yAxis: WidgetYAxis;

	thresholds: ImpulseFormShape<{
		mode: WidgetThresholdsMode;
		steps: ImpulseFormList<WidgetThresholdsStep>;
	}>;
}>;

type Input = GetImpulseFormInput<BarChartFormState>;

const DEFAULT = {
	range: {
		min: WidgetRange.default,
		max: WidgetRange.default,
	},
	legend: WidgetLegend.default,
	yAxis: WidgetYAxis.default,
	thresholds: {
		mode: WidgetThresholdsMode.default,
		steps: [],
	},
} satisfies Input;

function init(initial?: Partial<Input, "deep">): BarChartFormState {
	return ImpulseFormShape({
		range: ImpulseFormShape({
			min: WidgetRange.init(initial?.range?.min),
			max: WidgetRange.init(initial?.range?.max),
		}),

		legend: WidgetLegend.init(initial?.legend),

		yAxis: WidgetYAxis.init(initial?.yAxis),

		thresholds: ImpulseFormShape({
			mode: WidgetThresholdsMode.init(initial?.thresholds?.mode),
			steps: ImpulseFormList((initial?.thresholds?.steps ?? DEFAULT.thresholds.steps).map(WidgetThresholdsStep.init)),
		}),
	});
}

function setInput({ range, legend, yAxis, thresholds }: Partial<Input, "deep">, state: BarChartFormState): void {
	state.setInput({
		range,
		legend: {
			enabled: legend?.enabled,
			element: {
				position: legend?.element?.position,
				mode: {
					active: legend?.element?.mode?.active,
					branches: {
						table: legend?.element?.mode?.branches?.table?.filter(isPresent),
					},
				},
			},
		},
		yAxis,

		thresholds: {
			mode: thresholds?.mode,
		},
	});

	if (thresholds?.steps != null) {
		setFormElementsInput(
			(threshold) => WidgetThresholdsStep.init(threshold),
			thresholds.steps.filter(isPresent),
			state.fields.thresholds.fields.steps,
		);
	}
}

function fromSync({ min, max, unit, decimals, legend, yAxis, thresholds }: WidgetFormSync) {
	return {
		range: { min, max },

		legend: {
			enabled: legend?.enabled,
			element: {
				position: legend?.position,
				mode: {
					active: legend?.mode,
					branches: {
						table: legend?.table?.filter(isPresent),
					},
				},
			},
		},

		yAxis: {
			enabled: yAxis?.enabled,
			element: {
				decimals,
				unit,
				label: yAxis?.label,
			},
		},

		thresholds: {
			mode: thresholds?.mode,
			steps: thresholds?.steps,
		},
	} satisfies GetImpulseFormInputSetter<BarChartFormState>;
}

function toSync({ range, yAxis, legend, thresholds }: Input) {
	return {
		min: range.min,
		max: range.max,
		unit: yAxis.element.unit,
		decimals: yAxis.element.decimals,
		legend: {
			enabled: legend.enabled,
			position: legend.element.position,
			mode: legend.element.mode.active,
			table: legend.element.mode.branches.table,
		},
		yAxis: {
			enabled: yAxis.enabled,
			label: yAxis.element.label,
		},
		thresholds: {
			mode: thresholds.mode,
			steps: thresholds.steps,
		},
	} satisfies WidgetFormSync;
}

const BarChartFormState = {
	default: DEFAULT,
	init,
	setInput,
	fromSync,
	toSync,
} as const;

export { BarChartFormState };
